import { platform } from '@/lib/platform';

interface NativeLocation {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    speed: number;
    heading: number;
    altitude: number;
  };
  timestamp: string;
  battery: { level: number; is_charging: boolean };
  activity: { type: string; confidence: number };
}

type LocationCallback = (location: NativeLocation) => void;

class BackgroundLocationService {
  private plugin: any = null;
  private _isTracking = false;

  async init(): Promise<void> {
    if (!platform.isNative) return;
    const mod =
      await import('@transistorsoft/capacitor-background-geolocation');
    this.plugin = mod.default;
  }

  async start(options: {
    distanceFilter: number;
    onLocation: LocationCallback;
  }): Promise<void> {
    if (!this.plugin || this._isTracking) return;

    await this.plugin.ready({
      distanceFilter: options.distanceFilter,
      stopTimeout: 5,
      stationaryRadius: 25,
      stopOnTerminate: false,
      startOnBoot: true,
      foregroundService: true,
      enableHeadless: true,
      heartbeatInterval: 60,
      preventSuspend: true,
      // Allow simulated/mock locations in development
      allowIdenticalLocations: true,
      isMoving: true, // start in "moving" mode so it tracks immediately

      notification: {
        title: 'FieldOps',
        text: 'Location tracking active',
        channelName: 'Location Tracking',
        sticky: true,
      },
      debug: true,
      logLevel: this.plugin.LOG_LEVEL_WARNING,
    });

    this.plugin.onLocation(
      (location: NativeLocation) => options.onLocation(location),
      (error: any) => console.error('[BGLocation] Error:', error),
    );

    await this.plugin.start();
    this._isTracking = true;
  }

  async stop(): Promise<void> {
    if (!this.plugin) return;
    try {
      await this.plugin.stop();
      this.plugin.removeListeners();
    } catch {
      // Ignore stop errors — plugin may not have been started
    }
    this._isTracking = false;
  }

  get isTracking(): boolean {
    return this._isTracking;
  }
}

export const backgroundLocation = new BackgroundLocationService();
